import { CircularProgress, IconButton, Chip } from '@mui/material'
import { Link, routes } from '@cedarjs/router'
import useOrgName from 'src/hooks/useOrgName'
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined'
import { useMemo, useState } from 'react'
import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
  MRT_PaginationState,
  MRT_Cell,
  MRT_ColumnFiltersState,
} from 'material-react-table'
import { FORM_SUBMISSIONS_QUERY } from 'src/pages/FormsLandingPage/queries'
import { useQuery } from '@cedarjs/web'
import { getDefaultMRTOptions } from 'src/utils/getDefaultMRTOptions'
import { cloneDeep } from 'lodash'
import {
  FormSubmissionFilters,
  FormTemplate,
  FormSubmission,
} from 'types/graphql'

interface FormSubmissionsListProps {
  formTemplates?: FormTemplate[]
  formTemplateId?: string
}

const FormSubmissionsList = ({
  formTemplates,
  formTemplateId,
}: FormSubmissionsListProps) => {
  const orgName = useOrgName()
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  })
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const columnFilterObject: { [key in keyof FormSubmissionFilters]?: unknown } =
    useMemo(() => {
      return columnFilters.reduce((acc, filter) => {
        acc[filter.id] = cloneDeep(filter.value)
        return acc
      }, {})
    }, [columnFilters])

  const { data: submissionsData, loading: submissionsLoading } = useQuery(
    FORM_SUBMISSIONS_QUERY,
    {
      variables: {
        filters: {
          ...columnFilterObject,
          formTemplateId: formTemplateId
            ? [formTemplateId]
            : columnFilterObject.formTemplateId,
          status: 'SUBMITTED',
        },
        pagination,
      },
    }
  )

  const submissions = submissionsData?.formSubmissions?.edges || []
  const totalCount = submissionsData?.formSubmissions?.pageInfo?.totalCount || 0

  // MRT Columns
  const columns = useMemo<MRT_ColumnDef<FormSubmission>[]>(
    () => [
      {
        id: 'formTemplateId',
        header: 'Form',
        accessorFn: (row) => row.formTemplate?.id ?? '',
        Cell: ({ row }) => row.original.formTemplate?.title ?? '',
        enableColumnFilter: formTemplates?.length > 1,
        filterVariant: 'multi-select',
        // custom select so values are template ids
        filterSelectOptions: formTemplates?.map((template) => ({
          label: template.title,
          value: template.id,
        })),
      },
      {
        id: 'submissionType',
        header: 'Type',
        accessorFn: (row) => row.submissionType,
        enableColumnFilter: true,
        filterVariant: 'multi-select',
        filterSelectOptions: ['FORM', 'PDF'],
        Cell: ({ cell }: { cell: MRT_Cell<FormSubmission> }) => (
          <Chip
            label={cell.getValue<string>()}
            color={cell.getValue<string>() === 'PDF' ? 'primary' : 'default'}
            size="small"
          />
        ),
      },
      {
        id: 'submittingUser',
        header: 'Submitted By',
        accessorFn: (row) =>
          row.submittingUser
            ? `${row.submittingUser.firstName ?? ''} ${
                row.submittingUser.lastName ?? ''
              }`.trim()
            : 'Unknown User',
        enableColumnFilter: false,
      },
      {
        accessorKey: 'submittedAt',
        header: 'Submitted At',
        accessorFn: (row) => new Date(row.submittedAt).toLocaleString(),
        filterVariant: 'date-range',
      },
    ],
    [formTemplates]
  )

  const defaultTableOptions = getDefaultMRTOptions<FormSubmission>()

  const table = useMaterialReactTable<FormSubmission>({
    ...defaultTableOptions,
    columns,
    data: submissions,
    manualPagination: true,
    manualFiltering: true,
    enableColumnFilters: true,
    rowCount: totalCount,
    enableTopToolbar: true,
    positionActionsColumn: 'last',
    enableRowActions: true,
    enableGlobalFilter: false,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        size: 80,
      },
    },
    renderRowActions: ({ row }) => (
      <IconButton
        size="small"
        component={Link}
        to={routes.viewFormSubmission({ orgName, id: row.original.id })}
        title="View Submission"
      >
        <ArrowForwardOutlinedIcon />
      </IconButton>
    ),
    initialState: { density: 'comfortable' },
    state: {
      isLoading: submissionsLoading,
      pagination,
      columnFilters,
    },
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
  })

  return (
    <div className="mt-4">
      {submissionsLoading ? (
        <div className="flex items-center justify-center py-8">
          <CircularProgress />
        </div>
      ) : (
        <MaterialReactTable table={table} />
      )}
    </div>
  )
}

export default FormSubmissionsList
